<template>
  <InspectionModal
    :endpoint="endpoint"
    :loadDetail="loadDetail"
    :seed="selectedCancel">
    <template v-slot:default="props">
      <form>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Inspect Cancel Request</p>
          </header>
          <section class="modal-card-body">
            <!-- Loaded -->
            <div v-if="!props.loading && props.record !== null" class="">
              <p class="has-text-weight-bold mb-sm">Cancel Request Details</p>
              <table class="table is-fullwidth is-striped is-size-7 is-narrow border">
                <tbody>
                  <tr>
                    <th>ID</th>
                    <td>{{ props.record.id }}</td>
                  </tr>
                  <tr>
                    <th>fabric order ID</th>
                    <td>{{ props.record.order_id }}</td>
                  </tr>
                  <tr>
                    <th>Request Received</th>
                    <td>{{ $dates.utc(props.record.created_at).local().format('MMM D, YYYY @ h:mm A') }}</td>
                  </tr>
                  <tr>
                    <th>Approved On</th>
                    <td>
                      <span v-if="props.record.approved_at">{{ $dates.utc(props.record.approved_at).local().format('MMM D, YYYY @ h:mm A') }}</span>
                      <span v-else class="has-text-grey-light"> --- </span>
                    </td>
                  </tr>
                  <tr>
                    <th>Rejected On</th>
                    <td>
                      <span v-if="props.record.rejected_at">{{ $dates.utc(props.record.rejected_at).local().format('MMM D, YYYY @ h:mm A') }}</span>
                      <span v-else class="has-text-grey-light"> --- </span>
                    </td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td><span :class="ctaState.customStyle"> {{ ctaState.label }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <!-- Lines -->
              <div>
                <p class="has-text-weight-bold mb-sm">Items Requested for Cancellation</p>
                <table class="table is-fullwidth is-striped is-size-7 is-narrow border is-vcentered">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th class="has-text-centered" style="width:128px">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="line in props.record.cancel_request_lines" :key="line.id">
                      <td>
                        <Identifiers :variant="line.variant" :showImage="false" nameInteraction="none" />
                      </td>
                      <td class="has-text-centered">{{ line.quantity }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <!-- Loading -->
            <div v-else>
              <loading message="Loading item..." />
            </div>
          </section>
          <footer class="modal-card-foot space-between">
            <b-button @click="$parent.close()">Close</b-button>
            <div class="buttons" v-if="context.isSupplier()">
              <b-tooltip :active="ctaState.disabled" :label="ctaState.rejectTooltip" position="is-top">
                <b-button type="is-danger" :disabled="ctaState.disabled || savingRejectCancel" :loading="savingRejectCancel" @click="reject" class="mr-2">Reject
                  Cancel</b-button>
              </b-tooltip>
              <b-tooltip :active="ctaState.disabled" :label="ctaState.approveTooltip" position="is-left">
                <b-button type="is-success" :disabled="ctaState.disabled || savingApprovingCancel" :loading="savingApprovingCancel" @click="approve">Approve Cancel</b-button>
              </b-tooltip>
            </div>
          </footer>
        </div>
      </form>
    </template>
  </InspectionModal>
</template>

<script>
import Identifiers from '@/components/Models/Variant/Identifiers'
import InspectionModal from '../InspectionModal'
import { mapGetters } from 'vuex'

export default {
  components: {
    Identifiers,
    InspectionModal
  },
  data () {
    return {
      loadDetail: false,
      savingRejectCancel: false,
      savingApprovingCancel: false
    }
  },
  computed: {
    ...mapGetters(['context']),
    endpoint () {
      if (this.selectedCancel !== null) {
        return this.context.route + '/cancel-requests/' + this.selectedCancel.id + '/'
      }
      return null
    },
    ctaState () {
      const cr = this.selectedCancel
      const status = cr?.status

      const isExpired =
      status === 'expired' ||
      (!!cr?.expires_at && new Date(cr.expires_at) <= new Date())

      if (isExpired) {
        return {
          disabled: true,
          approveTooltip: 'This request has expired and can no longer be approved.',
          rejectTooltip: 'This request has expired and can no longer be rejected.',
          label: 'Expired',
          customStyle: 'has-text-danger has-text-weight-bold'
        }
      }

      if (status === 'approved') {
        return {
          disabled: true,
          approveTooltip: 'This request has already been approved.',
          rejectTooltip: 'This request has already been approved.',
          label: 'APPROVED',
          customStyle: 'has-text-success has-text-weight-bold'
        }
      }

      if (status === 'rejected') {
        return {
          disabled: true,
          approveTooltip: 'This request has already been rejected.',
          rejectTooltip: 'This request has already been rejected.',
          label: 'REJECTED',
          customStyle: 'has-text-danger has-text-weight-bold'
        }
      }

      if (status === 'pending') {
        return {
          disabled: false,
          approveTooltip: 'Approve this cancel request.',
          rejectTooltip: 'Reject this cancel request.',
          label: 'PENDING',
          customStyle: 'has-text-warning has-text-weight-bold'
        }
      }

      // fallback (unknown status)
      return {
        disabled: true,
        approveTooltip: 'Action unavailable.',
        rejectTooltip: 'Action unavailable.',
        label: status,
        customStyle: 'has-text-danger has-text-weight-bold'
      }
    }
  },
  methods: {
    approve () {
      this.savingApprovingCancel = true
      this.$http.put(`${this.endpoint}approve/`).then(response => {
        this.$buefy.toast.open({ type: 'is-success', message: 'Cancel request approved.' })
        this.$emit('cancel:acknowledged')
      }).catch(error => {
        let errorMessage = 'An unexpected error occurred!'
        if (error.response && error.response.data.detail) {
          errorMessage = error.response.data.detail
        }
        this.$buefy.toast.open({ type: 'is-danger', message: errorMessage })
      }).finally(() => {
        this.savingApprovingCancel = false
        this.$parent.close()
      })
    },
    reject () {
      this.savingRejectCancel = true
      this.$http.put(`${this.endpoint}reject/`).then(response => {
        this.$buefy.toast.open({ type: 'is-success', message: 'Cancel request rejected.' })
        this.$emit('cancel:acknowledged')
      }).catch(error => {
        let errorMessage = 'An unexpected error occurred!'
        if (error.response && error.response.data.detail) {
          errorMessage = error.response.data.detail
        }
        this.$buefy.toast.open({ type: 'is-danger', message: errorMessage })
      }).finally(() => {
        this.savingRejectCancel = false
        this.$parent.close()
      })
    }
  },
  props: ['selectedCancel']
}
</script>
